import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SharjahPoliceEmblem } from '../SharjahPoliceEmblem';
import { CarIcon, BikeIcon, TruckIcon, UserIcon, BoxIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
const PAIR2_LOGO = 'https://cdn.magicpatterns.com/uploads/51QRCG1WJpxN6EwND9yhQF/Pair2_(2).svg';
const TITLE_WORDS = ['التحول', 'نحو', 'منظومة', 'ذكية', 'لإدارة', 'الأساطيل', 'لتعزيز', 'الامن', 'و', 'السلامة', 'المرورية'];
const SUBTITLE = 'منصة Pair² لإدارة الأساطيل وربط المركبات والسائقين والجهات التشغيلية في الوقت الفعلي';
const TAGLINE = 'ما بعد تتبع المركبات — ذكاء لحظي للسائقين والمركبات';
const ATTRIBUTION = 'مقدم إلى هيئة النقل — حكومة عجمان';
const ENTITY_ICONS: BoxIcon[] = [CarIcon, BikeIcon, TruckIcon, CarIcon, UserIcon];
export function CoverSlide() {
  const {
    theme
  } = useTheme();
  const reduce = useReducedMotion();
  const base = reduce ? {
    duration: 0
  } : undefined;
  return <section dir="rtl" className="relative w-screen h-screen overflow-hidden flex flex-col" style={{
    background: '#EDF1F4'
  }} data-id="element-227">
      {/* ========= SUBTLE BACKGROUND DECORATION ========= */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden data-id="element-228">
        {/* Soft top-right aurora */}
        <div className="absolute -top-40 -right-40 w-[60vmax] h-[60vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.08) 0%, transparent 70%)'
      }} data-id="element-229" />
        {/* Soft bottom-left aurora */}
        <div className="absolute -bottom-40 -left-40 w-[50vmax] h-[50vmax] rounded-full blur-3xl" style={{
        background: 'radial-gradient(circle, rgba(47,169,156,0.05) 0%, transparent 70%)'
      }} data-id="element-230" />
        {/* Very subtle grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm1 1h38v38H1V1z' fill='%230B1328' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`
      }} data-id="element-231" />
      </div>

      {/* ========= TOP BAR — Pair² (left) · Sharjah Police (right) ========= */}
      <motion.div initial={{
      opacity: 0,
      y: -14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={base ?? {
      duration: 0.9,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative z-20 flex items-center justify-between pt-[clamp(1.5rem,3.5vh,3rem)] px-[clamp(1.75rem,4.5vw,4.5rem)]" data-id="element-232">
        {/* In RTL, the first child renders on the RIGHT.
            Sharjah Police first → shows on the right. Pair² second → shows on the left. */}
        <SharjahPoliceEmblem variant="light" data-id="element-233" />

        <img src={PAIR2_LOGO} alt="Pair²" className="h-[clamp(2.5rem,5vh,3.5rem)] w-auto" style={{
        filter: 'drop-shadow(0 4px 18px rgba(47,169,156,0.28))'
      }} draggable={false} data-id="element-234" />
      </motion.div>

      {/* ========= CENTER CONTENT — title + subtitle ========= */}
      <div className="relative z-20 flex-1 flex flex-col items-center justify-center px-[clamp(1.5rem,5vw,6rem)] text-center pt-[clamp(1rem,3vh,2rem)]" data-id="element-235">
        {/* Title */}
        <h1 className="font-arabic-display font-black leading-[1.18] max-w-[22ch]" style={{
        color: '#0B1328',
        fontSize: 'clamp(1.75rem, 4.2vw, 4.25rem)',
        letterSpacing: '-0.005em'
      }} data-id="element-236">
          {TITLE_WORDS.map((word, i) => {
          // Highlight specific words in teal
          const isHighlight = word === 'لإدارة' || word === 'الأساطيل';
          return <motion.span key={i} initial={{
            opacity: 0,
            y: 20,
            filter: 'blur(8px)'
          }} animate={{
            opacity: 1,
            y: 0,
            filter: 'blur(0px)'
          }} transition={base ?? {
            duration: 0.85,
            delay: 0.75 + i * 0.075,
            ease: [0.22, 1, 0.36, 1]
          }} className="inline-block mx-[0.22em]" style={isHighlight ? {
            color: '#2FA99C'
          } : {}} data-id="element-237">
                {word}
              </motion.span>;
        })}
        </h1>

        {/* Accent divider */}
        <motion.div initial={{
        scaleX: 0,
        opacity: 0
      }} animate={{
        scaleX: 1,
        opacity: 1
      }} transition={base ?? {
        duration: 1.3,
        delay: 1.55,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-[clamp(1.1rem,2.5vh,1.75rem)] h-[3px] rounded-full w-[min(12rem,25vw)] origin-center" style={{
        background: '#2FA99C'
      }} data-id="element-238" />

        {/* Subtitle */}
        <motion.p initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={base ?? {
        duration: 1,
        delay: 1.75
      }} className="font-arabic-body mt-[clamp(1.5rem,3.5vh,2.5rem)] max-w-[56ch] leading-[1.85]" style={{
        color: '#1A1F2B',
        fontSize: 'clamp(0.95rem, 1.3vw, 1.25rem)'
      }} data-id="element-239">
          {SUBTITLE}
        </motion.p>
      </div>

      {/* ========= BOTTOM CONTENT — Entity Icons & Attribution ========= */}
      <div className="relative z-20 pb-[clamp(1.5rem,3vh,2.5rem)] flex flex-col items-center gap-[clamp(1.5rem,3vh,2.5rem)]" data-id="element-240">
        {/* Entity icons row */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={base ?? {
        duration: 1,
        delay: 2.3
      }} className="flex items-center justify-center gap-[clamp(1.25rem,2.5vw,2rem)]" aria-hidden data-id="element-241">
          {ENTITY_ICONS.map((Icon, i) => <div key={i} className="rounded-full flex items-center justify-center" style={{
          width: 'clamp(3rem, 4.5vw, 4rem)',
          height: 'clamp(3rem, 4.5vw, 4rem)',
          background: 'rgba(47,169,156,0.08)',
          border: '1.5px solid rgba(47,169,156,0.25)'
        }} data-id="element-242">
              <Icon style={{
            width: 'clamp(1.5rem, 2.2vw, 2rem)',
            height: 'clamp(1.5rem, 2.2vw, 2rem)',
            color: '#2FA99C'
          }} strokeWidth={1.4} data-id="element-243" />
            </div>)}
        </motion.div>

        {/* Attribution */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={base ?? {
        duration: 1,
        delay: 2.6
      }} data-id="element-244">
          <p className="font-arabic-body tracking-[0.12em] text-center font-medium" style={{
          color: 'rgba(26,31,43,0.5)',
          fontSize: 'clamp(0.75rem, 0.9vw, 0.95rem)'
        }} data-id="element-245">
            {theme.attribution}
          </p>
        </motion.div>
      </div>
    </section>;
}